import { useCallback, useEffect, useState } from "react";
import { ApiError, api } from "./client";
import type { ManeuverCode, RatingCriterionCode } from "../constants/lessonReportCatalog";
import { averageLessonRating } from "../constants/lessonReportCatalog";

/** @deprecated v1 — geriye uyumluluk */
export const TOPIC_KEYS = [
  "direksiyon_hakimiyeti",
  "vites_kullanimi",
  "park_dik",
  "park_paralel",
  "geri_manevra",
  "serit_degistirme",
  "viraj",
  "hiz_kontrolu",
  "sinyal_kullanimi",
  "ayna_kullanimi",
  "trafik_uyumu",
  "rampa_kalkis",
  "park_aciklamali",
  "sehir_ici_surus",
  "sehir_disi_surus",
  "gece_surusu",
] as const;

export type TopicKey = (typeof TOPIC_KEYS)[number];

export const TOPIC_LABELS: Record<TopicKey, string> = {
  direksiyon_hakimiyeti: "Direksiyon Hakimiyeti",
  vites_kullanimi: "Vites Kullanimi",
  park_dik: "Dik Park",
  park_paralel: "Paralel Park",
  geri_manevra: "Geri Manevra",
  serit_degistirme: "Serit Degistirme",
  viraj: "Viraj",
  hiz_kontrolu: "Hiz Kontrolu",
  sinyal_kullanimi: "Sinyal Kullanimi",
  ayna_kullanimi: "Ayna Kullanimi",
  trafik_uyumu: "Trafik Uyumu",
  rampa_kalkis: "Rampa Kalkis",
  park_aciklamali: "Aciklamali Park",
  sehir_ici_surus: "Sehir Ici Surus",
  sehir_disi_surus: "Sehir Disi Surus",
  gece_surusu: "Gece Surusu",
};

export type LessonReport = {
  id: string;
  appointment_id: string;
  student_id: string;
  instructor_id: string;
  vehicle_id: string;
  attended: boolean;
  start_km: number | null;
  end_km: number | null;
  km_delta: number | null;
  topics_covered: TopicKey[];
  driving_score: number | null;
  maneuver_counts: Partial<Record<ManeuverCode, number>>;
  rating_scores: Partial<Record<RatingCriterionCode, number>>;
  lesson_rating_avg: number | null;
  exam_ready: boolean;
  general_notes: string | null;
  next_lesson_suggestion: string | null;
  no_show_reason: string | null;
  created_at: string;
  updated_at: string;
};

export type RichLessonReport = LessonReport & {
  student_name: string;
  instructor_name: string;
  vehicle_plate: string | null;
  appointment: {
    starts_at: string;
    ends_at: string;
    lesson_type: string;
    status: string;
  };
};

export type SubmitReportInput = {
  appointmentId: string;
  attended: boolean;
  startKm?: number | null;
  endKm?: number | null;
  topicsCovered?: TopicKey[];
  drivingScore?: number | null;
  maneuverCounts?: Partial<Record<ManeuverCode, number>>;
  ratingScores?: Partial<Record<RatingCriterionCode, number>>;
  examReady?: boolean;
  confirmHighKmDelta?: boolean;
  generalNotes?: string | null;
  nextLessonSuggestion?: string | null;
  noShowReason?: string | null;
};

export function reportLessonRating(report: Pick<LessonReport, "lesson_rating_avg" | "driving_score">): number | null {
  if (report.lesson_rating_avg !== null && report.lesson_rating_avg !== undefined) {
    return report.lesson_rating_avg;
  }
  return report.driving_score;
}

export { averageLessonRating };

export type SubmitReportResponse = {
  saved: boolean;
  was_inserted: boolean;
  report: LessonReport;
  appointment_status: "completed";
  km_auto_updated: boolean;
  km_delta: number;
};

export type ListReportsResponse = {
  items: RichLessonReport[];
  total: number;
  limit: number;
  offset: number;
};

export type LessonReportSortField =
  | "starts_at"
  | "student_name"
  | "instructor_name"
  | "vehicle_plate"
  | "attended"
  | "lesson_rating_avg"
  | "general_notes"
  | `maneuver_${ManeuverCode}`;

export type LessonReportSortDir = "asc" | "desc";

export type ReportListFilter = {
  branchId?: string;
  instructorId?: string;
  studentId?: string;
  attended?: boolean | "";
  startDate?: string;
  endDate?: string;
  q?: string;
  sort?: LessonReportSortField;
  order?: LessonReportSortDir;
  limit?: number;
  offset?: number;
};

export async function submitLessonReport(input: SubmitReportInput): Promise<SubmitReportResponse> {
  return api.post<SubmitReportResponse>("/lesson-reports", input);
}

export async function fetchReportByAppointment(appointmentId: string): Promise<RichLessonReport | null> {
  const result = await api.get<RichLessonReport | null>(
    `/lesson-reports/by-appointment/${appointmentId}`,
  );
  if (!result || (typeof result === "object" && Object.keys(result).length === 0)) return null;
  return result;
}

export function useLessonReportList(filter: ReportListFilter): {
  data: ListReportsResponse | null;
  loading: boolean;
  error: string | null;
  reload: () => void;
} {
  const [data, setData] = useState<ListReportsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  const { branchId, instructorId, studentId, attended, startDate, endDate, q, sort, order, limit, offset } =
    filter;

  useEffect(() => {
    const controller = new AbortController();
    setLoading(true);
    setError(null);
    api
      .get<ListReportsResponse>(
        "/lesson-reports",
        {
          branch_id: branchId || undefined,
          instructor_id: instructorId || undefined,
          student_id: studentId || undefined,
          attended: attended === "" || attended === undefined ? undefined : String(attended),
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          q: q || undefined,
          sort: sort || undefined,
          order: order || undefined,
          limit: limit ?? 20,
          offset: offset ?? 0,
        },
        { signal: controller.signal },
      )
      .then((res) => setData(res))
      .catch((err) => {
        if (controller.signal.aborted) return;
        setError(err instanceof ApiError ? err.message : "Raporlar Yüklenemedi.");
      })
      .finally(() => {
        if (!controller.signal.aborted) setLoading(false);
      });
    return () => controller.abort();
  }, [branchId, instructorId, studentId, attended, startDate, endDate, q, sort, order, limit, offset, reloadKey]);

  const reload = useCallback(() => setReloadKey((k) => k + 1), []);

  return { data, loading, error, reload };
}
